// src/components/users/AddUserModal.jsx

import { useState } from 'react';
import { X, User, Mail, Phone, Bell } from 'lucide-react';
import { Button, Input } from '../ui';

const INITIAL_FORM_STATE = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  receives_test_out_notifications: false,
  email_preferences: true,
  sms_preferences: false,
  notify: true,
};

export function AddUserModal({ isOpen, onClose, onSuccess, organizationId }) {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  if (!isOpen) return null;

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when field is updated
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name.trim()) newErrors.first_name = 'First name is required';
    if (!formData.last_name.trim()) newErrors.last_name = 'Last name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      newErrors.phone = 'Phone must be 10 digits';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      // Build payload
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        phone: formData.phone.replace(/\D/g, ''), // Strip non-digits
        role: 'Admin',
        is_provider: true,
        email_preferences: formData.email_preferences,
        sms_preferences: formData.sms_preferences,
        notify: formData.notify,
        solution_id: 5,
        organization_id: organizationId,
        acl_care: {
          receives_test_out_notifications: formData.receives_test_out_notifications,
        },
      };

      const { api } = await import('../../services/api');
      const result = await api.addUser(payload);

      // Reset form and close
      setFormData(INITIAL_FORM_STATE);
      onSuccess(result);
      onClose();
    } catch (err) {
      console.error('[AddUserModal] Error adding user:', err);
      setSubmitError(err.message || 'Failed to add user. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData(INITIAL_FORM_STATE);
      setErrors({});
      setSubmitError(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={handleClose} />
      
      <div className="flex min-h-full items-end justify-center p-4 sm:items-center">
        <div className="relative bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Add Admin</h2>
            <button
              onClick={handleClose}
              disabled={isSubmitting}
              className="p-2 rounded-full hover:bg-gray-100 disabled:opacity-50"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-6">
            {/* Basic Info */}
            <div>
              <div className="flex items-center mb-3">
                <User className="h-4 w-4 text-gray-500 mr-2" />
                <h3 className="font-medium text-gray-900">Basic Information</h3>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <Input
                    value={formData.first_name}
                    onChange={(e) => updateField('first_name', e.target.value)}
                    placeholder="First name"
                    error={errors.first_name}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <Input
                    value={formData.last_name}
                    onChange={(e) => updateField('last_name', e.target.value)}
                    placeholder="Last name"
                    error={errors.last_name}
                  />
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div>
              <div className="flex items-center mb-3">
                <Mail className="h-4 w-4 text-gray-500 mr-2" />
                <h3 className="font-medium text-gray-900">Contact Information</h3>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email *
                  </label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="email@example.com"
                    error={errors.email}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone *
                  </label>
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="4447778899"
                    error={errors.phone}
                  />
                </div>
              </div>
            </div>

            {/* Notification Preferences */}
            <div>
              <div className="flex items-center mb-3">
                <Bell className="h-4 w-4 text-gray-500 mr-2" />
                <h3 className="font-medium text-gray-900">Notification Preferences</h3>
              </div>

              <div className="space-y-3">
                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.receives_test_out_notifications}
                    onChange={(e) => updateField('receives_test_out_notifications', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <div className="ml-2">
                    <span className="text-sm text-gray-700">
                      Test out notifications
                    </span>
                    <p className="text-xs text-gray-500">
                      Receive notifications when clients pass test outs
                    </p>
                  </div>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.email_preferences}
                    onChange={(e) => updateField('email_preferences', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Email notifications for important updates
                  </span>
                </label>

                <label className="flex items-start">
                  <input
                    type="checkbox"
                    checked={formData.sms_preferences}
                    onChange={(e) => updateField('sms_preferences', e.target.checked)}
                    className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                  />
                  <div className="ml-2">
                    <span className="text-sm text-gray-700">
                      Text message notifications for important updates
                    </span>
                    <p className="text-xs text-amber-600 mt-1">
                      USER MUST HAVE PROVIDED EXPRESSED VERBAL CONSENT.
                    </p>
                  </div>
                </label>

                <div className="pt-2 border-t border-gray-100">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      checked={formData.notify}
                      onChange={(e) => updateField('notify', e.target.checked)}
                      className="mt-1 h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                    />
                    <div className="ml-2">
                      <span className="text-sm text-gray-700">
                        Notify User
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        User will receive a welcome email to get started
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>

            {/* Submit Error */}
            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}
          </form>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 flex gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting}
              fullWidth
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                'Add Admin'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}